import { head, isNil, path, pathOr, pipe, pluck, zipObj } from 'ramda'
import { RSAA } from 'redux-api-middleware'
import {
  extractBenefits,
  getApiPath,
  isHouseholdPolicy,
  webStorage,
} from 'sidecar-utility-library'

import { initialState, types } from './types'
import { types as memberTypes } from 'store/Member'

const DEFAULT_ERROR_MSG = 'Something went wrong'

export const fetchPolicy = uuid => ({
  [RSAA]: {
    endpoint: getApiPath('plcy', `policies/${uuid}`),
    method: 'GET',
    types: [
      {
        type: types.FETCH_POLICY_REQUEST,
        meta: { uuid },
      },
      types.FETCH_POLICY_SUCCESS,
      types.FETCH_POLICY_ERROR,
    ],
  },
})

/**
 * Fetches policies for a given member.
 *
 * This function creates an action to fetch policies associated with a specific
 * member UUID. It uses the Redux Standard API-calling Action (RSAA) to define
 * the API endpoint, HTTP method, and action types for the request, success, and
 * error states.
 *
 * @param {string} memberUuid - The UUID of the member whose policies are to be
 *   fetched.
 * @returns {Object} An action object with RSAA configuration for fetching
 *   policies.
 */
export const fetchMemberPolicies = memberUuid => ({
  [RSAA]: {
    endpoint: getApiPath('plcy', `members/${memberUuid}/policies`),
    method: 'GET',

    types: [
      {
        type: types.FETCH_MEMBER_POLICIES_REQUEST,
        meta: { memberUuid },
      },
      types.FETCH_MEMBER_POLICIES_SUCCESS,
      types.FETCH_MEMBER_POLICIES_ERROR,
    ],
  },
})

// breaking this out from fetchPolicy because we need to
// different types for individual member policies
export const fetchPolicyDetails = uuid => ({
  [RSAA]: {
    endpoint: getApiPath('plcy', `policies/${uuid}`),
    method: 'GET',
    types: [
      types.FETCH_POLICY_DETAILS_REQUEST,
      types.FETCH_POLICY_DETAILS_SUCCESS,
      types.FETCH_POLICY_DETAILS_ERROR,
    ],
  },
})

export const cancelPolicy = (uuid, reason, comments) => ({
  [RSAA]: {
    endpoint: getApiPath(`acct`, `members/${uuid}/cancel`),
    method: 'POST',
    body: JSON.stringify({ reason, comments }),
    types: [
      types.CANCEL_POLICY_REQUEST,
      types.CANCEL_POLICY_SUCCESS,
      types.CANCEL_POLICY_ERROR,
    ],
  },
})

export const updateUpcomingHouseholdPolicy = payload => ({
  [RSAA]: {
    endpoint: getApiPath(`enr`, `household-policy/update`),
    method: 'POST',
    body: JSON.stringify(payload),
    types: [
      types.UPDATE_UPCOMING_HOUSEHOLD_POLICY_REQUEST,
      types.UPDATE_UPCOMING_HOUSEHOLD_POLICY_SUCCESS,
      types.UPDATE_UPCOMING_HOUSEHOLD_POLICY_ERROR,
    ],
  },
})

export const fetchCancelEstimate = uuid => ({
  [RSAA]: {
    endpoint: getApiPath(`acct`, `members/${uuid}/cancel-estimate`),
    method: 'GET',
    types: [
      types.FETCH_CANCEL_ESTIMATE_REQUEST,
      types.FETCH_CANCEL_ESTIMATE_SUCCESS,
      types.FETCH_CANCEL_ESTIMATE_ERROR,
    ],
  },
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case memberTypes.FETCH_MEMBERS_REQUEST:
    case types.FETCH_POLICY_REQUEST: {
      const uuid = meta && meta.uuid

      return isHouseholdPolicy(uuid)
        ? {
            ...state,
            masterPolicy: {
              ...state.masterPolicy,
              busy: true,
            },
          }
        : {
            ...state,
            policies: {
              ...state.policies,
              busy: true,
            },
          }
    }

    case types.FETCH_POLICY_SUCCESS: {
      const isUpcomingPolicy = payload.status === 'UPCOMING'
      const uuid = payload && payload.uuid

      // conditional statement to check for Master Policy or regular
      if (isHouseholdPolicy(uuid)) {
        return isUpcomingPolicy
          ? {
              ...state,
              masterPolicy: {
                ...state.masterPolicy,
                busy: false,
                entity: payload,
              },
              upcomingPolicy: payload,
            }
          : {
              ...state,
              masterPolicy: {
                ...state.masterPolicy,
                busy: false,
                entity: payload,
              },
            }
      } else {
        // nicely formats benefits at the root from the benefitInfo array
        const policyWithParsedBenefits = {
          ...payload,
          benefits: extractBenefits(payload),
        }

        return isUpcomingPolicy
          ? {
              ...state,
              policies: {
                ...state.policies,
                busy: false,
              },
              upcomingPolicy: policyWithParsedBenefits,
            }
          : {
              ...state,
              policies: {
                ...state.policies,
                busy: false,
                entities: {
                  ...state.policies.entities,
                  [payload.member?.memberUuid]: policyWithParsedBenefits,
                },
              },
            }
      }
    }

    case types.FETCH_POLICY_ERROR: {
      const uuid = payload && payload.uuid
      return isHouseholdPolicy(uuid)
        ? {
            ...state,
            masterPolicy: {
              ...state.masterPolicy,
              busy: false,
              error: pathOr(
                DEFAULT_ERROR_MSG,
                ['response', 'message'],
                payload,
              ),
            },
          }
        : {
            ...state,
            policies: {
              ...state.policies,
              busy: false,
              error: pathOr(
                DEFAULT_ERROR_MSG,
                ['response', 'message'],
                payload,
              ),
            },
          }
    }

    // Fetch all policies for a member
    case types.FETCH_MEMBER_POLICIES_REQUEST: {
      return {
        ...state,
        selectedMemberPolicies: {
          ...state.selectedMemberPolicies,
          error: null,
          busy: true,
        },
      }
    }
    case types.FETCH_MEMBER_POLICIES_SUCCESS: {
      return {
        ...state,
        selectedMemberPolicies: {
          ...state.selectedMemberPolicies,
          busy: false,
          entity: payload,
        },
      }
    }
    case types.FETCH_MEMBER_POLICIES_ERROR: {
      return {
        ...state,
        selectedMemberPolicies: {
          ...state.selectedMemberPolicies,
          busy: false,
          error: payload.response.message,
        },
      }
    }

    // ------------

    case memberTypes.FETCH_AUTHED_USER_SUCCESS: {
      const { policy } = payload

      // nicely formats benefits at the root from the benefitInfo array
      const policyWithParsedBenefits = {
        ...policy,
        benefits: extractBenefits(policy),
      }
      // TODO: remove the check if policy is defined
      // once db migration scripts are run on previous members
      return {
        ...state,
        policy: {
          ...state.policy,
          entity: policy ? policyWithParsedBenefits : {},
        },
      }
    }

    // ------------

    case memberTypes.FETCH_MEMBERS_SUCCESS: {
      const memberUuids = pluck('uuid')(payload)
      const policies = payload.map(m => m.policy)

      // if master policy exists, it's the same value for all
      // members. So we will pick member[0]
      const masterPolicy = pipe(
        head,
        path(['policy', 'parentPolicy', 'uuid']),
      )(payload)

      const authedMember =
        payload.find(m => m.uuid === webStorage.getItem('sub')) || {}

      // Create the base state update object.
      const baseState = {
        ...state,
        policies: {
          ...state.policies,
          busy: false,
          entities: zipObj(memberUuids, policies),
        },
        // keep the authed users policy up-to-date with whatever
        // comes back from the backend on this api response
        policy: {
          ...state.policy,
          entity: {
            ...authedMember.policy,
            benefits: extractBenefits(authedMember.policy),
          },
          error: '',
        },
      }

      // Conditionally add masterPolicies if masterPolicy is not empty.
      // Only ACA members have have a master policy. For non-ACA members,
      // this is undefined and causes breaks. So we only want to update
      // masterPolicies if a masterPolicy exists (the member is ACA).
      return isNil(masterPolicy)
        ? baseState
        : {
            ...baseState,
            masterPolicy: {
              ...state.masterPolicy,
              busy: false,
              entity: { ...state.masterPolicy.entity, uuid: masterPolicy },
              error: '',
            },
          }
    }

    case memberTypes.FETCH_MEMBERS_ERROR: {
      return {
        ...state,
        policies: {
          ...state.policies,
          busy: false,
          error: pathOr(DEFAULT_ERROR_MSG, ['response', 'message'], payload),
        },
        masterPolicy: {
          ...state.masterPolicy,
          busy: false,
          error: pathOr(DEFAULT_ERROR_MSG, ['response', 'message'], payload),
        },
      }
    }

    // ---------

    case types.CANCEL_POLICY_REQUEST:
    case types.FETCH_CANCEL_ESTIMATE_REQUEST: {
      return {
        ...state,
        cancelPolicy: {
          ...state.cancelPolicy,
          busy: true,
        },
      }
    }

    case types.CANCEL_POLICY_SUCCESS: {
      // if the policy was successfully cancelled,
      // then BE returns 204 with no content and we will
      // fetch their policy after close of the modal
      return {
        ...state,
        cancelPolicy: {
          ...state.cancelPolicy,
          busy: false,
        },
      }
    }

    case types.CANCEL_POLICY_ERROR:
    case types.FETCH_CANCEL_ESTIMATE_ERROR: {
      return {
        ...state,
        cancelPolicy: {
          ...state.cancelPolicy,
          busy: false,
          error: pathOr(DEFAULT_ERROR_MSG, ['response', 'message'], payload),
        },
      }
    }

    // ----------

    case types.FETCH_CANCEL_ESTIMATE_SUCCESS: {
      return {
        ...state,
        cancelPolicy: {
          ...state.cancelPolicy,
          busy: false,
          estimate: payload,
        },
      }
    }

    // ----------

    case memberTypes.DECIDE_ON_EXCLUSIONS_SUCCESS: {
      const { policy, uuid } = payload
      const isAuthedMember = uuid === webStorage.getItem('sub')

      return isAuthedMember
        ? {
            ...state,
            policies: {
              ...state.policies,
              entities: {
                ...state.policies.entities,
                [uuid]: policy,
              },
            },
            policy: {
              ...state.policy,
              entity: {
                ...policy,
                benefits: extractBenefits(policy),
              },
            },
          }
        : {
            ...state,
            policies: {
              ...state.policies,
              entities: {
                ...state.policies.entities,
                [uuid]: policy,
              },
            },
          }
    }

    // -------

    case types.UPDATE_UPCOMING_HOUSEHOLD_POLICY_REQUEST: {
      return {
        ...state,
        masterPolicy: {
          ...state.masterPolicy,
          busy: true,
          error: '',
        },
      }
    }
    case types.UPDATE_UPCOMING_HOUSEHOLD_POLICY_SUCCESS: {
      return {
        ...state,
        masterPolicy: {
          ...state.masterPolicy,
          busy: false,
        },
      }
    }
    case types.UPDATE_UPCOMING_HOUSEHOLD_POLICY_ERROR: {
      return {
        ...state,
        masterPolicy: {
          ...state.masterPolicy,
          busy: false,
          error: pathOr(DEFAULT_ERROR_MSG, ['response', 'message'], payload),
        },
      }
    }

    case types.FETCH_POLICY_DETAILS_REQUEST: {
      return {
        ...state,
        policyDetails: {
          ...state.policyDetails,
          busy: true,
          error: '',
        },
      }
    }
    case types.FETCH_POLICY_DETAILS_SUCCESS: {
      return {
        ...state,
        policyDetails: {
          ...state.policyDetails,
          entity: payload,
          busy: false,
        },
      }
    }
    case types.FETCH_POLICY_DETAILS_ERROR: {
      return {
        ...state,
        policyDetails: {
          ...state.policyDetails,
          busy: false,
          error: pathOr(DEFAULT_ERROR_MSG, ['response', 'message'], payload),
        },
      }
    }

    default:
      return state
  }
}
